import React from "react";
import useSeo from "../hooks/useSeo";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail } from "lucide-react";

const Section = ({ id, title, children }) => (
  <div id={id} className="mb-10 scroll-mt-24">
    <h2 className="font-display font-semibold text-2xl text-brand-dark tracking-tight">{title}</h2>
    <div className="mt-3 text-brand-text2 leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function PrivacyPolicyPage() {
  useSeo({
    title: "Privatlivspolitik · EMA Kurer ApS",
    description:
      "Sådan behandler EMA Kurer ApS dine persondata jf. GDPR og dansk databeskyttelseslov. Læs om dine rettigheder, opbevaringsperioder og databehandlere.",
    path: "/privatlivspolitik",
  });

  return (
    <div data-testid="privacy-policy-page">
      <section className="bg-brand-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red flex items-center gap-2">
            <ShieldCheck size={14} /> Juridisk
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-3">Privatlivspolitik</h1>
          <p className="text-white/70 mt-3">Version 2.0 · Senest opdateret: 17. februar 2026</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <Section id="dataansvarlig" title="1. Dataansvarlig">
          <p><strong>EMA Kurer ApS</strong> · CVR 46465717</p>
          <p>Kontakt: <a href="mailto:kontakt@emakurer.dk" className="text-brand-red">kontakt@emakurer.dk</a></p>
          <p>Web: <a href="https://emakurer.dk" className="text-brand-red">emakurer.dk</a></p>
          <p>Vi er ansvarlige for behandlingen af de persondata, som du afgiver til os i forbindelse med brugen af vores tjenester.</p>
        </Section>

        <Section id="data-vi-indsamler" title="2. Hvilke data indsamler vi?">
          <p>Vi indsamler kun de data, der er nødvendige for at levere vores kurérservice:</p>
          <p className="font-semibold text-brand-dark mt-3">2a. Ved booking</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Navn, telefonnummer og email (afsender + modtager)</li>
            <li>Afhentnings- og leveringsadresse</li>
            <li>Pakkens dimensioner og vægt (selvrapporteret)</li>
            <li>Bemærkninger og leveringsinstruktioner</li>
            <li>Betalingsoplysninger (håndteres af Stripe — vi opbevarer ikke kortdata)</li>
          </ul>

          <p className="font-semibold text-brand-dark mt-3">2b. Ved levering</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Foto-bevis af afleveringssted (slettes efter 90 dage)</li>
            <li>Tidsstempler for afhentning og levering</li>
            <li>GPS-koordinater for chaufførens lokation (kun under aktiv levering)</li>
          </ul>

          <p className="font-semibold text-brand-dark mt-3">2c. Ved brug af websitet</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>IP-adresse, browsertype, sprog (anonym statistik)</li>
            <li>Anonyme brugsstatistikker via PostHog (kun med dit samtykke jf. cookie-banner)</li>
            <li>Foretrukne adresser via DAWA-autocomplete (vi gemmer ikke dette permanent)</li>
          </ul>

          <p className="font-semibold text-brand-dark mt-3">2d. For chauffør-ansøgere</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Straffeattest (anonymiseret efter 90 dage hvis du ikke ansættes)</li>
            <li>Kørekort-kopi (slettes efter ansættelsesophør + 5 år iht. bogføringsloven)</li>
            <li>Ansættelseskontrakt (opbevares iht. bogføringsloven + 5 år)</li>
          </ul>

          <p className="font-semibold text-brand-dark mt-3">2e. For erhvervskunder (B2B-portal)</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Virksomhedsoplysninger (CVR, navn, adresse, kontaktperson)</li>
            <li>Portal-brugere (e-mail, navn, rolle — bcrypt-hashed password)</li>
            <li>Pickup-locations (adresser, åbningstider, kontaktpersoner)</li>
            <li>Forsendelses-data (afsender + modtager, ref-nummer, dimensioner, status-historik)</li>
            <li>Tracking-events (tidsstempler, status, kilde — portal/api/csv/admin/system/driver)</li>
            <li>Failed-delivery data (årsagskode, eventuelt foto-bevis, GPS — slettes efter 90 dage)</li>
            <li>Invoice basis-data (forsendelser, gebyrer, totaler — opbevares 5 år iht. bogføringsloven)</li>
          </ul>

          <p className="font-semibold text-brand-dark mt-3">2f. Ved brug af public tracking</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Sider du besøger via tracking-link (anonym statistik)</li>
            <li>Public tracking viser kun: forsendelsesnummer, status, modtager-fornavn (uden efternavn), postnr/by, produkt og event-historik. Ingen pris, adresse, telefon, e-mail eller fulde navne</li>
          </ul>

          <p className="font-semibold text-brand-dark mt-3">2g. Ved brug af API + webhooks (B2B)</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>API-key metadata (navn, prefix, sandbox/live, sidst brugt)</li>
            <li>Anmodnings-logs (endpoint, status, idempotency-key, brugte 90 dage)</li>
            <li>Webhook-leveringer (mål-URL, payload-hash, leverings-status, retry-count — gemmes 30 dage)</li>
          </ul>

          <p className="font-semibold text-brand-dark mt-3">2h. For underleverandører/vognmænd</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Virksomhedsoplysninger + CVR</li>
            <li>Skift-data (rute-tildelinger, faktiske start/slut-tider, pauser, GPS-bekræftelse ved afslutning)</li>
            <li>Afregningsgrundlag (godkendte timer, sats, ekstra-gebyrer — opbevares 5 år)</li>
            <li>E-mail kommunikation (afregningsgrundlag sendes efter hver afsluttet rute)</li>
          </ul>
        </Section>

        <Section id="formaal" title="3. Formålet med behandlingen">
          <p>Vi behandler dine persondata til følgende formål, jf. databeskyttelsesforordningens art. 6:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Opfyldelse af kontrakt (art. 6.1.b):</strong> Levering af pakke, kommunikation, faktura</li>
            <li><strong>Retlig forpligtelse (art. 6.1.c):</strong> Bogføring, momsindberetning, opbevaring i 5 år iht. bogføringsloven</li>
            <li><strong>Legitim interesse (art. 6.1.f):</strong> Forebyggelse af bedrageri, anti-spam, sikkerhedslogning</li>
            <li><strong>Samtykke (art. 6.1.a):</strong> Anonyme analytics (PostHog), nyhedsbrev, marketing</li>
          </ul>
        </Section>

        <Section id="opbevaring" title="4. Opbevaringsperioder">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Booking-data:</strong> Slettes 12 måneder efter levering (bortset fra fakturaer)</li>
            <li><strong>Fakturaer + regnskabsbilag:</strong> 5 år iht. bogføringsloven</li>
            <li><strong>Foto-bevis af levering:</strong> 90 dage</li>
            <li><strong>GPS-spor:</strong> Slettes efter pakken er leveret (24 timer)</li>
            <li><strong>Chat-historik:</strong> 12 måneder</li>
            <li><strong>Nyhedsbrev-tilmelding:</strong> Indtil du afmelder dig (én klik via link i hver mail)</li>
            <li><strong>PostHog analytics:</strong> Anonyme data, 12 måneder rolling</li>
            <li><strong>Cookies:</strong> Se vores <Link to="/handelsbetingelser#cookies" className="text-brand-red">cookie-politik</Link></li>
          </ul>
        </Section>

        <Section id="databehandlere" title="5. Databehandlere">
          <p>Vi deler kun dine data med databehandlere, der har underskrevet GDPR-databehandleraftale med os. Vores aktuelle databehandlere:</p>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2 border border-slate-200">Databehandler</th>
                  <th className="text-left p-2 border border-slate-200">Formål</th>
                  <th className="text-left p-2 border border-slate-200">Placering</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr><td className="p-2 border border-slate-200">Stripe Payments Europe Ltd.</td><td className="p-2 border border-slate-200">Betalingsbehandling (privat + B2B)</td><td className="p-2 border border-slate-200">Irland (EU)</td></tr>
                <tr><td className="p-2 border border-slate-200">Twilio Ireland Ltd.</td><td className="p-2 border border-slate-200">SMS-notifikationer (aktiveres senere)</td><td className="p-2 border border-slate-200">Irland (EU)</td></tr>
                <tr><td className="p-2 border border-slate-200">Simply.com A/S</td><td className="p-2 border border-slate-200">Email-server (SMTP)</td><td className="p-2 border border-slate-200">Danmark</td></tr>
                <tr><td className="p-2 border border-slate-200">Billy ApS</td><td className="p-2 border border-slate-200">Bogføring + fakturaer (privat + B2B)</td><td className="p-2 border border-slate-200">Danmark</td></tr>
                <tr><td className="p-2 border border-slate-200">DAWA (Erhvervsstyrelsen)</td><td className="p-2 border border-slate-200">Adresseautocomplete</td><td className="p-2 border border-slate-200">Danmark</td></tr>
                <tr><td className="p-2 border border-slate-200">cvrapi.dk</td><td className="p-2 border border-slate-200">CVR-opslag for B2B-kunder</td><td className="p-2 border border-slate-200">Danmark</td></tr>
                <tr><td className="p-2 border border-slate-200">MongoDB Atlas</td><td className="p-2 border border-slate-200">Database-hosting</td><td className="p-2 border border-slate-200">EU (Frankfurt)</td></tr>
                <tr><td className="p-2 border border-slate-200">OpenRouteService (HeiGIT)</td><td className="p-2 border border-slate-200">Rute-beregning + distance-matrix</td><td className="p-2 border border-slate-200">Tyskland (EU)</td></tr>
                <tr><td className="p-2 border border-slate-200">PostHog Inc.</td><td className="p-2 border border-slate-200">Anonym analytics (kun ved samtykke)</td><td className="p-2 border border-slate-200">USA (Standard Contractual Clauses)</td></tr>
                <tr><td className="p-2 border border-slate-200">Emergent Labs Inc.</td><td className="p-2 border border-slate-200">Hosting (Kubernetes)</td><td className="p-2 border border-slate-200">EU</td></tr>
                <tr><td className="p-2 border border-slate-200">IDURA</td><td className="p-2 border border-slate-200">MitID broker (chauffør-onboarding)</td><td className="p-2 border border-slate-200">Danmark</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3">Vi videregiver IKKE dine data til tredjeparter til markedsføringsformål.</p>
        </Section>

        <Section id="dine-rettigheder" title="6. Dine rettigheder">
          <p>Du har følgende rettigheder iht. GDPR:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Indsigt:</strong> Få en kopi af alle data vi har om dig</li>
            <li><strong>Berigtigelse:</strong> Få rettet ukorrekte oplysninger</li>
            <li><strong>Sletning ("retten til at blive glemt"):</strong> Få dine data slettet (undtaget data vi er forpligtet til at opbevare iht. lov)</li>
            <li><strong>Begrænsning:</strong> Begrænse behandlingen af dine data</li>
            <li><strong>Dataportabilitet:</strong> Modtage dine data i et struktureret, almindeligt anvendt format (JSON/CSV)</li>
            <li><strong>Indsigelse:</strong> Modsige dig behandling baseret på legitim interesse</li>
            <li><strong>Tilbagekaldelse af samtykke:</strong> Trække samtykke tilbage til enhver tid (gælder fremadrettet)</li>
          </ul>
          <p className="mt-3">For at udøve dine rettigheder, kontakt os på <a href="mailto:gdpr@emakurer.dk" className="text-brand-red">gdpr@emakurer.dk</a>. Vi svarer inden for 30 dage. Vi kræver muligvis identitetsverificering.</p>
        </Section>

        <Section id="klage" title="7. Klagemulighed">
          <p>Hvis du er utilfreds med vores behandling af dine persondata, kan du klage til:</p>
          <p>
            <strong>Datatilsynet</strong><br />
            Carl Jacobsens Vej 35, 2500 Valby<br />
            Telefon: +45 33 19 32 00<br />
            Email: <a href="mailto:dt@datatilsynet.dk" className="text-brand-red">dt@datatilsynet.dk</a><br />
            Web: <a href="https://www.datatilsynet.dk" className="text-brand-red">datatilsynet.dk</a>
          </p>
        </Section>

        <Section id="sikkerhed" title="8. Sikkerhed">
          <p>Vi anvender følgende sikkerhedsforanstaltninger:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>TLS 1.3 kryptering af al kommunikation</li>
            <li>Bcrypt-kryptering af passwords (12+ rounds)</li>
            <li>JWT-tokens med kort levetid for sessioner</li>
            <li>Adgangskontrol med to-faktor-autentifikation for medarbejdere</li>
            <li>Audit-log af alle administratorhandlinger</li>
            <li>Daglig backup af database</li>
            <li>Penetrationstest mindst én gang om året</li>
          </ul>
          <p className="mt-3">Vi anmelder enhver databrud til Datatilsynet inden for 72 timer iht. GDPR art. 33, og kontakter berørte brugere direkte.</p>
        </Section>

        <Section id="cookies-link" title="9. Cookies">
          <p>For information om vores brug af cookies, se vores <Link to="/cookie-politik" className="text-brand-red">cookie-politik</Link>. Du kan altid ændre dine cookie-valg via "Cookies"-linket i footeren.</p>
        </Section>

        <Section id="esg" title="10. ESG- og CO₂e-rapportering (kommer senere)">
          <p>EMA Kurer arbejder på et CO₂e-rapporteringsmodul. Når dette aktiveres, vil vi beregne CO₂e-estimater pr. forsendelse, rute og kunde — udelukkende baseret på driftsdata (kilometer, køretøjsklasse, leveringsantal). Vi indsamler ikke nye persondata til dette formål; vi anvender data, der allerede er indsamlet til driftsmæssige formål.</p>
          <p>ESG-data behandles på samme retsgrundlag som driftsdata (kontraktopfyldelse + legitim interesse). Læs mere på <Link to="/esg" className="text-brand-red">/esg</Link>.</p>
        </Section>

        <Section id="aendringer" title="11. Ændringer i privatlivspolitikken">
          <p>Vi opdaterer denne politik når lovgivningen ændres eller når vi ændrer vores praksis. Den aktuelle version er altid tilgængelig på <Link to="/privatlivspolitik" className="text-brand-red">emakurer.dk/privatlivspolitik</Link>. Væsentlige ændringer notificeres via email til registrerede brugere.</p>
          <p>Tidligere versioner kan rekvireres ved henvendelse til <a href="mailto:gdpr@emakurer.dk" className="text-brand-red">gdpr@emakurer.dk</a>.</p>
        </Section>

        <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <div className="flex items-start gap-3">
            <Mail size={20} className="text-brand-red shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-lg font-bold text-brand-dark">Spørgsmål?</h3>
              <p className="text-sm text-brand-text2 mt-1">
                Kontakt vores GDPR-ansvarlige på <a href="mailto:gdpr@emakurer.dk" className="text-brand-red">gdpr@emakurer.dk</a> eller <a href="mailto:kontakt@emakurer.dk" className="text-brand-red">kontakt@emakurer.dk</a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
